import {
  ClientActivityQueryParams,
  ClientTaskQueryParams,
  GroupedClientListQueryParams,
} from '../model/client.model';
import { ActivityQueryParams, TaskQueryParams } from '../model/dashboard.model';
import { MemberTaskQueryParams } from '../model/team.model';

export class ApiConstants {
  public static readonly AUTH_API = {
    VALIDATE: {
      URL: (user: string) => `validate_user?userName=${user}`,
    },
    CREATE_USER: {
      URL: () => `create_user`,
    },
    RESEND_CONFIRMATION: {
      URL: () => `resend_confirmation`,
    },
    SET_PASSWORD: {
      URL: () => `set_password`,
    },
    FORGOT_PASSWORD: {
      URL: () => `forgot_password`,
    },
    CHANGE_PASSWORD: {
      URL: () => `/change_password`,
    },

    LOGIN: {
      URL: () => `login`,
    },
    LOGOUT: {
      URL: () => `logout`,
    },
    REFRESH: {
      URL: () => `/refresh_token`,
    },
    GET_HUDDLE_INFO: {
      URL: () => `/get_huddle_info`,
    },
    MS_LOGIN: {
      URL: (id: string, uri: string) => `/ms_login_tokens?code=${id}&redirect_uri=${uri}`,
    },
    MS_TEAM_LOGIN: {
      URL: () => `/ms_auth/login?redirect_uri=${process.env.REACT_APP_URI}/ms_auth/redirect`,
    },
    MS_TEAM_EVENTS: {
      URL: (code: string, email: string, isExistingAccessTokenValid: boolean) =>
        `/team_meetings?email=${email}&redirect_uri=${process.env.REACT_APP_REDIRECT}${
          code && '&code=' + code
        }${isExistingAccessTokenValid && '&isExistingAccessTokenValid=true'}`,
    },
    GOOGLE_AUTHCALLBACK: {
      URL: (code: string, url: string) =>
        `/google-calendar/oauth2callback?code=${code}&redirect_uri=${url}`,
    },
    ZOOM_AUTHCALLBACK: {
      URL: (code: string, url: string) => `/zoom/authCallback?code=${code}&redirect_uri=${url}`,
    },
    GOOGLE_REFRESH: {
      URL: () => `/google-calendar/refresh-token`,
    },
    RESPOND_TO_CHALLENGE: {
      URL: () => `/respond_to_auth_challenge`,
    },
  };
  public static readonly CALENDAR_API = {
    AVAILABLE_CURRENT_ACCOUNT: {
      URL: () => `/calendar_accounts`,
    },
    EDIT_CURRENT_ACCOUNT: {
      URL: (email: string) => `/calendar_account/${email}`,
    },
    GOOGLE_CALENDAR: {
      URL: () => `/google-calendar/events?`,
      QUERY_PARAMS: (queryParams?: any): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    ZOOM_CALENDAR: {
      URL: () => `/zoom/meetings`,
    },
    CREATE_ZOOM_EVENT: {
      URL: (email: string) => `/zoom/${email}/meeting`,
    },
    CREATE_GOOGLE_EVENT: {
      URL: (email: string) => `/google-calendar/event/${email}`,
    },
    CREATE_MS_EVENT: {
      URL: (email: string) => `/${email}/team_meeting`,
    },
    EDIT_GOOGLE_EVENT: {
      URL: (mail: string, id: string) => `/google-calendar/event/${mail}/${id}`,
    },
    EDIT_ZOOM_EVENT: {
      URL: (mail: string, meetingId: string) => `/zoom/meeting/${mail}/${meetingId}`,
    },
    EDIT_MS_EVENT: {
      URL: (calendarEmail: string, meetingId: string) =>
        `/${calendarEmail}/team_meeting/${meetingId}`,
    },
    DELETE_ZOOM_EVENT: {
      URL: (calendarEmail: string, meetingId: string) =>
        `/zoom/meeting/${calendarEmail}/${meetingId}`,
    },
    DELETE_MS_EVENT: {
      URL: (calendarEmail: string, meetingId: string) =>
        `/${calendarEmail}/team_meeting/${meetingId}`,
    },
    DELETE_GOOGLE_EVENT: {
      URL: (calendarEmail: string, eventId: string) =>
        `/google-calendar/event/${calendarEmail}/${eventId}`,
    },
    CALENDAR_UNLINK: {
      URL: (integration: string, calendarEmail: string) =>
        `/unlink/${integration}/${calendarEmail}`,
    },
  };
  public static readonly DASHBOARD_API = {
    GET_DEAL: {
      URL: () => `sf/get_rollup_nums`,
    },
    GET_TASKS: {
      URL: () => `sf/get_tasks?`,
      QUERY_PARAMS: (queryParams?: TaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    GET_ACTIVITY: {
      URL: () => `sf/recent_activity?`,
      QUERY_PARAMS: (queryParams?: ActivityQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    GET_CLIENT: {
      URL: () => `sf/client_filter_values`,
    },
    GET_ACTION_ACCOUNT: {
      URL: () => `sf/actionable_accounts`,
    },
    GET_SPORTS_NEWS: {
      URL: () => `sports-news`,
    },
  };
  public static readonly CLIENT_API = {
    ALL_CLIENT: {
      URL: () => `clients/all_clients`,
    },
    CLIENT_LIST_V2: {
      URL: () => `/clients-v2/list?`,
      QUERY_PARAMS: (queryParams?: GroupedClientListQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        return validParams
          .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
          .join('&');
      },
    },
    CLIENT_TYPES_V2: {
      URL: () => `/clients-v2/types`,
    },
    ADD_DEAL: {
      URL: () => `/clients/deal`,
    },
    EDIT_DEAL: {
      URL: (id: string) => `/clients/deal/${id}`,
    },
    ALL_PRICEBOOKS: {
      URL: () => `/clients/pricebooks`,
    },
    CLIENT_DETAILS: {
      URL: (id: string) => `clients/client_profile_details?accountId=${id}`,
    },
    DEAL_TYPES: {
      URL: () => `/clients/deal_types`,
    },
    DEAL_DROPDOWN: { URL: (id?: string) => `/clients/deal_dropdowns${id ? '?id=' + id : ''}` },
    CREATE_PRICEBOOK_PRODUCTS: {
      URL: () => `/clients/opplineitems`,
    },
    EDIT_PRICEBOOK_PRODUCTS: {
      URL: (id: string) => `/clients/opplineitems/${id}`,
    },
    DELETE_DEAL: {
      URL: (id: string) => `clients/deal/${id}`,
    },
    PRICEBOOK_PRODUCTS: {
      URL: (id: string) => `/clients/pricebook/${id}/products`,
    },
    CLIENT_TASKS: {
      URL: (id: string) => `clients/client_tasks?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: ClientTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    CLIENT_DROPDOWNS: {
      URL: (id: string) => `/clients/client_dropdowns?clientType=${id}`,
    },
    CLIENT_ADD: {
      URL: () => `/clients/client`,
    },
    CLIENT_ACTIVITY: {
      URL: (id: string) => `clients/client_activity?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: ClientActivityQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    CLIENT_CONTACTS: {
      URL: (id: string) => `clients/client_contacts?accountId=${id}`,
    },
    CLIENT_DEAL: {
      URL: (id: string) => `clients/client_rollup_nums?accountId=${id}`,
    },
    CLIENT_KEY_DEALS: {
      URL: (id: string) => `clients/key_deals?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: MemberTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    DELETE_EDIT_CLIENT: {
      URL: (id: string) => `/clients/client/${id}`,
    },
  };
  public static readonly HUDDLE_API = {
    SAVE_PROFILE: {
      URL: (email: string) => `/sf/user/profile/${email}`,
    },
  };
  public static readonly TEAM_API = {
    ALL_MEMBER: {
      URL: () => `team/members`,
    },
    MEMBER_DEALS: {
      URL: (id: string) => `team/member/deals?memberId=${id}`,
    },
    MEMBER_TASKS: {
      URL: (id: string) => `team/tasks?memberId=${id}&`,
      QUERY_PARAMS: (queryParams?: MemberTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    MEMBER_WORKLOAD: {
      URL: (id: string) => `team/workload/${id}`,
    },
    ASSIGNMENT: {
      URL: () => `team/assignment`,
    },
    UPDATE_ASSIGNMENT: {
      URL: (assignmentId: string) => `team/assignment/${assignmentId}`,
    },
    DELETE_ASSIGNMENT: {
      URL: (assignmentId: string) => `team/assignment/${assignmentId}`,
    },
  };
  public static readonly SF_API = {
    GET_DROPDOWN: {
      URL: (email: string) => `/sf/task_dropdowns?mailId=${email}`,
    },
    GET_SF_TOKEN: {
      URL: (code: string) => `/sf/get_tokens?code=${code}`,
    },
    SF_TASK: {
      URL: () => `/sf/task`,
    },
    EDIT_SF_TASK: {
      URL: (code: string) => `/sf/task/${code}`,
    },
    DELETE_SF_TASK: {
      URL: (code: string) => `/sf/task/${code}`,
    },
    GET_PRESIGNED_URL: {
      URL: (mail: string, type: string, key: string) =>
        `/sf/get_presigned_url?mailId=${mail}&contentType=${type}&key=${key}`,
    },
    S3_UPLOAD: {
      URL: () => process.env.REACT_APP_S3 || '',
    },
    GET_PROFILE_DETAILS: {
      URL: (email: string) => `sf/user/profile/${email}`,
    },
    UPDATE_PROFILE: {
      URL: (email: string) => `sf/user/profile/${email}`,
    },
  };
  public static readonly NOTES_API = {
    ALL_NOTES: {
      URL: (id: string) => `/sf/notes/${id}?`,
      QUERY_PARAMS: (queryParams?: { access_token: string }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    ADD_EDIT_NOTES: {
      URL: () => `/sf/notes`,
    },
    DELETE_NOTES: {},
  };
  public static readonly CAPTIVE8_API = {
    MAIN_INFO: { URL: (id: string) => `/captiv8/info/${id}` },
    CREATOR: { URL: (id: string) => `/captiv8/creator/performance/${id}` },
    SEARCH_ID: { URL: (user: string) => `/captiv8/search-users?query=${user}` },
  };
  public static readonly ASANA_API = {
    ASANA_LOGIN: {
      URL: () => `/asana/exchange-token`,
    },
    ASANA_LOGOUT: {
      URL: () => `/asana/auth/logout`,
    },
    ASANA_VALIDATE_TOKEN: {
      URL: () => `/asana/auth/validate-token`,
    },
    ASANA_REFRESH: {
      URL: () => `/asana/refresh-token`,
    },

    STATISTICS: {
      URL: () => `/asana/stats`,
    },
    TEAMS: {
      URL: () => `/asana/teams`,
    },
    PROJECTS: {
      URL: () => `/asana/projects?`,
      QUERY_PARAMS: (queryParams?: {
        team: string;
        member: string;
        owner: string;
        portfolio: string;
      }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `${key}=${value}`).join('&');
      },
    },
    FILTER_OPTIONS: {
      URL: () => `/asana/filter-options`,
    },
    CREATE_PROJECT: {
      URL: () => `/asana/create-project`,
    },
    EDIT_PROJECT: {
      URL: (id: string) => `/asana/projects/${id}`,
    },
    DELETE_PROJECT: {
      URL: (id: string) => `/asana/projects/${id}`,
    },
    TASKS_BY_SECTION: {
      URL: (id: string) => `/asana/projects/${id}/tasks?`,
      QUERY_PARAMS: (queryParams?: {
        priority: string;
        status: string;
        assignee: string;
        created_by: string;
        overdue: string;
      }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `${key}=${value}`).join('&');
      },
    },
    MOVE_BY_SECTION: {
      URL: (id: string) => `/asana/sections/${id}/move-task`,
    },
    TASK_FILTER: {
      URL: (id: string) => `/asana/projects/${id}/task-filter-options`,
    },
    CREATE_TASKS: {
      URL: (id: string) => `/asana/projects/${id}/tasks`,
    },
    EDIT_DELETE_TASKS: {
      URL: (id: string) => `/asana/tasks/${id}`,
    },
    ASANA_TASK_ATTACHMENTS: {
      URL: (id: string) => `/asana/tasks/${id}/attachments`,
    },
    ASANA_TASK_BY_ID: {
      URL: (id: string) => `/asana/tasks/${id}`,
    },
    ASANA_ATTACHMENTS_REMOVE: {
      URL: (id: string) => `/asana/attachments/${id}`,
    },
    ASANA_INVITE_MEMBERS: {
      URL: (id: string) => `/asana/projects/${id}/invite`,
    },
    ASANA_ADD_SECTION: {
      URL: (id: string) => `/asana/projects/${id}/sections`,
    },
    ASANA_EDIT_DELETE_SECTION: {
      URL: (id: string) => `/asana/sections/${id}`,
    },
  };
  public static readonly SLACK_API = {
    SLACK_LOGIN: {
      URL: (code: string) => `/slack/authCallback?code=${code}`,
    },

    SLACK_CHANNEL_MESSAGE: {
      URL: (id: string, next?: string) =>
        `/slack/channels/${id}/messages?limit=10${next ? `&cursor=${next}` : ''}`,
    },

    SLACK_UNLINK: {
      URL: () => `/unlink_huddle_info`,
    },
    SLACK_CHANNELS: {
      URL: () => `/slack/channels/`,
    },
    SLACK_REPLY: {
      URL: (channelId: string) => `/slack/channels/${channelId}/reply`,
    },
    SLACK_REACTIONS: {
      URL: (channelId: string) => `/slack/channels/${channelId}/reactions`,
    },
  };
  public static readonly LOCATION = {
    GOOGLE_LOCATION: {
      URL: (loc: string) => `/clients/client_address_suggestions?location=${loc}`,
    },
  };
  public static readonly ZOOMINFO = {
    TOP_UPDATES: {
      URL: (page = 1) => `/zoominfo/top-updates?page=${page}&pageSize=5`,
    },
    SEARCH_ZOOMINFO: {
      URL: (query: string) => `/zoominfo/search?q=${query}`,
    },
    TARGET_ACCOUNTS: {
      URL: (page = 1) => `/zoominfo/targets?page=${page}&pageSize=5`,
    },
    SUGGESTION: {
      URL: (page = 1) => `/zoominfo/suggestions?limit=5&page=${page}`,
    },
    ADD_TARGET: {
      URL: () => `/zoominfo/targets`,
    },
    COMPANY_DETAILS: {
      URL: (companyId: string) => `/zoominfo/company/${companyId}`,
    },
    COMPANY_EMPLOYEE: {
      URL: (companyId: string, page: string) =>
        `/zoominfo/company/${companyId}/employees?page=${page}&pageSize=10`,
      QUERY_PARAMS: (queryParams?: { department: string }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `&${key}=${value}`).join('&');
      },
    },
    COMPANY_NEWS: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/news?page=${page}&pageSize=10`,
    },
    COMPANY_SIMILAR: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/similar?page=${page}&pageSize=10`,
    },
    COMPANY_SIGNALS: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/signals?days=30&page=${page}&pageSize=20`,
    },
    ADD_TAG: {
      URL: (id: string) => `/zoominfo/company/${id}/tags`,
    },
    DELETE_TAG: {
      URL: (id: string, tag: string) => `/zoominfo/company/${id}/tags/${tag}`,
    },
    GET_TAGS: {
      URL: () => `/zoominfo/tags`,
    },
    ADD_DELETE_LISTS: {
      URL: (id: string) => `/zoominfo/company/${id}/lists`,
    },
    EXPORT_COMPANY_DATA: {
      URL: (id: string) => `/zoominfo/company/${id}/export`,
    },
    DELETE_TARGET: {
      URL: (id: string) => `/zoominfo/targets/${id}`,
    },
    EXPORT_CONTACT: {
      URL: (id: string) => `/zoominfo/company/${id}/contacts/export`,
    },
    EMPLOYEE_DROPDOWN: {
      URL: (id: string) => `/zoominfo/company/${id}/employees/filters`,
    },
    DISMISS_FEED: {
      URL: (id: string) => `/zoominfo/company/${id}/block`,
    },
    CORPORATE_HIERCHY: {
      URL: (id: string) => `https://app.zoominfo.com/#/apps/profile/company/${id}/locations`,
    },
  };
  public static readonly FILES_API = {
    GET_PRESIGNED: {
      URL: (clientId: string, params: URLSearchParams) =>
        `/clients/${clientId}/files/presigned-url?${params}`,
    },
    POST_FILES: {
      URL: (clientId: string) => `/clients/${clientId}/files`,
    },
    GET_FILES: {
      URL: (clientId: string) => `/clients/${clientId}/files`,
    },
    GET_FILE_DETAILS: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}`,
    },
    DOWNLOAD_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/download`,
    },
    SHARE_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/share`,
      QUERY_PARAMS: (queryParams?: { expiresIn: number }): string => {
        const validParams = Object.entries(queryParams || {}).filter(([_, value]) => value != null);

        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    DELETE_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}`,
    },
    RENAME_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/rename`,
    },
  };
  public static readonly ADMIN_API = {
    ALL_USERS: {
      URL: (limit: number, page: number) => `/admin/users?limit=${limit}&page=${page}`,
      QUERY_PARAMS: (queryParams?: any): string => {
        const validParams = Object.entries(queryParams || {}).filter(([_, value]) => value != null);

        return validParams.map(([key, value]) => `&${key}=${value}`).join('&');
      },
    },
    SEARCH_USERS: {
      URL: (query: string, limit: number) => `/admin/users/search?q=${query}&limit=${limit}`,
    },
    CREATE_USER: {
      URL: () => `/admin/users`,
    },
    UPDATE_USER: {
      URL: (email: string) => `/admin/users/${email}`,
    },
    DELETE_USER: {
      URL: (email: string) => `/admin/users/${email}`,
    },
    BLOCK_USER: {
      URL: (email: string) => `/admin/users/${email}/block`,
    },
    UNBLOCK_USER: {
      URL: (email: string) => `/admin/users/${email}/unblock`,
    },
    BATCH_DELETE_USERS: {
      URL: () => `/admin/users/batch/delete`,
    },
    BATCH_BLOCK_USERS: {
      URL: () => `/admin/users/batch/block`,
    },
    BATCH_UNBLOCK_USERS: {
      URL: () => `/admin/users/batch/unblock`,
    },
    BATCH_CHANGE_ROLES: {
      URL: () => `/admin/users/batch/role`,
    },
    GET_ROLES: {
      URL: () => `/admin/roles`,
    },
    GET_ROLE_DETAILS: {
      URL: (roleId: string) => `/admin/roles/${roleId}`,
    },
    CREATE_ROLE: {
      URL: () => `/admin/roles`,
    },
    UPDATE_ROLE: {
      URL: (roleId: string) => `/admin/roles/${roleId}`,
    },
    DELETE_ROLE: {
      URL: (roleId: string) => `/admin/roles/${roleId}`,
    },
    GET_ALL_PERMISSIONS: {
      URL: () => `/admin/permissions`,
    },
    GET_ROLE_PERMISSIONS: {
      URL: (roleId: string) => `/admin/roles/${roleId}/permissions`,
    },
    UPDATE_ROLE_PERMISSIONS: {
      URL: (roleId: string) => `/admin/roles/${roleId}/permissions/bulk`,
    },
    ADMIN_ROLE_FILTERS: {
      URL: () => `/admin/roles/filters`,
    },
    GET_ALL_INTEGRATIONS: {
      URL: () => `/admin/integrations`,
    },
    CREATE_INTEGRATION: {
      URL: () => `/admin/integrations`,
    },
    UPDATE_INTEGRATION: {
      URL: (integrationId: string) => `/admin/integrations/${integrationId}`,
    },
    UPDATE_INTEGRATION_ICON: {
      URL: (integrationId: string) => `/admin/integrations/${integrationId}/icon`,
    },
    DELETE_INTEGRATION: {
      URL: (integrationId: string) => `/admin/integrations/${integrationId}`,
    },
    GET_INTEGRATION_DETAILS: {
      URL: (integrationId: string) => `/admin/integrations/${integrationId}`,
    },
    UPDATE_INTEGRATION_CONFIG: {
      URL: (integrationId: string) => `/admin/integrations/${integrationId}/config`,
    },
  };
  public static readonly LEADS_API = {
    INIT_LEAD: {
      URL: () => `/leads/lead/init`,
    },
    GET_ALL_LEADS: {
      URL: (
        page: number,
        limit: number,
        mine: boolean = false,
        search: string = '',
        sports?: string,
        sort?: string,
      ) =>
        `/leads/all_leads?page=${page}&limit=${limit}&mine=${mine}${search ? `&search=${search}` : ''}${sports ? `&sport=${sports}` : ''}${sort ? `&sort=${sort}` : ''}`,
      QUERY_PARAMS: (queryParams?: any): string => {
        const validParams = Object.entries(queryParams || {}).filter(([_, value]) => value != null);

        return validParams.map(([key, value]) => `&${key}=${value}`).join('&');
      },
    },
    GET_LEAD_BY_ID: {
      URL: (id: string) => `/leads/lead/${id}`,
    },
    GET_LEAD_TYPES: {
      URL: () => `/leads/lead_types`,
    },
    CREATE_LEAD: {
      URL: () => `/leads/lead`,
    },
    GET_LEADS_DROPDOWNS: {
      URL: (leadType: string) => `/leads/lead_dropdowns/${leadType}`,
    },
    EDIT_LEAD: {
      URL: (id: string) => `/leads/lead/${id}`,
    },
    DELETE_LEAD: {
      URL: (id: string) => `/leads/lead/${id}`,
    },
    AGENCY_DROPDOWN: {
      URL: () => `/leads/agency_filter_values`,
    },
    ASSIGNEE_DROPDOWN: {
      URL: () => `/leads/members`,
    },
    DEMOGRAPHIC_PRO_DATA: {
      URL: (id: string) => `/leads/lead/analytics/${id}`,
    },
    GET_PRESIGNED_URL: {
      profile: {
        URL: (fileName: string, type: string, size: string) =>
          `/leads/profile-image-upload-url?fileName=${fileName}&fileType=${type}&fileSize=${size}`,
      },
      attachment: {
        URL: (fileName: string, type: string, size: string, leadId: string) =>
          `/leads/${leadId}/files/presigned-url?fileName=${fileName}&contentType=${type}&fileSize=${size}`,
      },
    },

    CONVERT_TO_CLIENT: {
      URL: (id: string) => `/leads/lead/${id}/convert-to-client`,
    },
    CAPTIV8_LEADS: {
      URL: (id: string) => `/leads/lead/captiv8/${id}`,
    },
    GET_ALL_LEAD_FILES: {
      URL: (id: string) => `/leads/${id}/files`,
    },
    GET_ANALYSIS: {
      URL: () => `/leads/lead/analytics`,
    },
    LEAD_CREDITS: {
      URL: () => `/leads/lead/analytics/credits`,
    },
  };
  public static readonly DEALS_API = {
    DEAL_TYPES: { URL: () => `/deals/new_DealTypes` },
    DEAL_STAGES: { URL: () => `/deals/deal_stages` },
    STAGES_HISTORY: { URL: (id: string) => `/deals/deal_stage_history/${id}` },
    SERVICES_BY_DEAL_TYPE: { URL: (type: string) => `/deals/services/${type}` },
    SERVICES_BY_DEAL_TYPE_ID_AND_CLIENT_ID: { URL: () => `/deals/deal_pricing_setup` },
    DEAL_DROPDOWNS: { URL: (type: string) => `/deals/deal_dropdowns/${type}` },
    ALL_CLIENTS: { URL: () => `/deals/all_clients` },
    CREATE_DEAL: { URL: () => `/deals/new_deal` },
    GET_DEAL: { URL: (id: string) => `/deals/deal/${id}` },
    UPDATE_DEAL: { URL: (id: string) => `/deals/deal/${id}` },
    DELETE_DEAL: { URL: (id: string) => `/deals/deal/${id}` },
    LIST_VIEW: { URL: () => `/deals/list_view` },
    KANBAN: { URL: () => `/deals/kanban` },
    RECENT_ACTIVITIES: { URL: () => `/deals/deal_activity` },
    MEMBERS: { URL: () => `/deals/members` },
    GET_PRICING_AND_SERVICES: { URL: (id: string) => `/deals/pricing_intelligence/${id}` },
    PRICEBOOKS: { URL: () => `/deals/pricebooks` },
    PRICEBOOK_PRODUCTS: { URL: (id: string) => `/deals/pricebook/${id}/products` },
    ROLLUP_NUMBER: { URL: () => `/deals/rollup_numbers` },
    GET_NOTES: { URL: (dealId: string) => `deals/notes/${dealId}` },
    POST_NOTE: { URL: () => `/deals/notes` },
    PATCH_NOTE: { URL: () => `/deals/notes` },
    DELETE_NOTE: { URL: (noteId: string) => `/deals/notes/${noteId}` },
  };
  public static readonly TASKS_NOTES_API = {
    GET_NOTES: { URL: (taskId: string) => `/tasks/notes/${taskId}` },
    POST_NOTE: { URL: () => `/tasks/notes` },
    PATCH_NOTE: { URL: () => `/tasks/notes` },
    DELETE_NOTE: { URL: (noteId: string) => `/tasks/notes/${noteId}` },
  };
  public static readonly CONTACT_API = {
    GET_ALL_CONTACTS: {
      URL: (mine: boolean) => `/contacts/all_contacts?mine=${mine}`,
      QUERY_PARAMS: (queryParams?: any): string => {
        const validParams = Object.entries(queryParams || {}).filter(([_, value]) => value != null);

        return validParams.map(([key, value]) => `&${key}=${value}`).join('');
      },
    },
    GET_CONTACT_BY_ID: { URL: (id: string) => `/contacts/contact/${id}` },
    GET_BUSINESS_CONTACTS: { URL: () => `contacts/business_contacts` },
    ADD_TO_CONTACTS: { URL: () => `/contacts/add_to_contacts` },
    ADD_CONTACT: { URL: () => `/contacts/contact` },
    EDIT_CONTACT: { URL: (id: string) => `/contacts/contact/${id}` },
    DELETE_CONTACT: { URL: (id: string) => `/contacts/contact/${id}` },
    CONTACT_DROPDOWNS: {
      URL: () => `/contacts/contact_dropdowns`,
    },
    GET_NOTES: { URL: (contactId: string) => `/contacts/notes/${contactId}` },
    POST_NOTE: { URL: () => `/contacts/notes` },
    PATCH_NOTE: { URL: () => `/contacts/notes` },
    DELETE_NOTE: { URL: (noteId: string) => `/contacts/notes/${noteId}` },
    PRESIGNED_URL: {
      URL: (size: string, type: string, key: string) =>
        `/contacts/profile-image-upload-url?fileName=${key}&fileType=${type}&fileSize=${size}`,
    },
  };
  public static readonly PROFILE_API = {
    UPLOAD_PROFILE_IMAGE: {
      URL: () =>
        process.env.REACT_APP_S3 ||
        'https://huddle-profile-pictures-dev.s3.us-east-1.amazonaws.com/',
    },
  };
  public static readonly GLOBAL_SEARCH = {
    GET_GLOBAL_SEARCH_RESULT: { URL: () => `/search` },
    POST_RECENT_SEARCH: { URL: () => `/search/click` },
    GET_RECENT_SEARCH_RESULTS: { URL: () => `/search/recent` },
  };
  public static readonly WIDGET_API = {
    GET_CONFIG: {
      URL: () => `/widget/config`,
    },
    UPDATE_CONFIG: {
      URL: (widgetId: string) => `/widget/config/${widgetId}`,
    },
  };
  public static readonly BUSINESSES_API = {
    GET_BUSINESSES: { URL: () => `/businesses` },
    GET_BUSINESS_BY_ID: { URL: (businessId: string) => `/businesses/${businessId}` },
    GET_BUSINESS_DEALS: { URL: (businessId: string) => `/businesses/${businessId}/deals` },
    GET_BUSINESS_INTEL: { URL: (businessId: string) => `/businesses/${businessId}/intel` },
    GET_BUSINESS_NEWS: { URL: (businessId: string) => `/businesses/${businessId}/news` },
    GET_SIMILAR_COMPANIES: { URL: (businessId: string) => `/businesses/${businessId}/similar` },
    GET_PLATFORM_INSIGHTS: { URL: (businessId: string) => `/businesses/${businessId}/insights` },
    GET_CONTACTS: { URL: (businessId: string) => `/businesses/${businessId}/contacts` },
    POST_CONTACT_TO_MY_CONTACTS: {
      URL: (businessId: string) => `/businesses/${businessId}/contacts/from-zoominfo`,
    },
    FOLLOW_BUSINESS: { URL: (businessId: string) => `/businesses/${businessId}/follow` },
    POST_BUSINESS_TARGET: { URL: () => `/businesses/from-zoominfo` },
    DELETE_TARGET: { URL: (businessId: string) => `/businesses/${businessId}` },

    GET_ZOOMINFO_SEARCH_RESULTS: { URL: () => `/zoominfo/search/light` },
    GET_ZOOMINFO_SEARCH_RECENT: { URL: () => `/zoominfo/search/recent` },
    POST_ZOOMINFO_SEARCH_RECENT: { URL: () => `/zoominfo/search/click` },
  };
}
